import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { toast } from "sonner";
import { getAccessToken } from "@/features/auth/services/accessToken.service";
import { refreshToken } from "@/features/auth/services/auth.service";
import { Sentry } from "@/shared/services/sentry.service";
import { getApiUrl } from "@/shared/utils/api";
import { GLOBAL_FORBIDDEN_ACTION_ERROR_MESSAGE, GLOBAL_FORBIDDEN_ERROR_DESCRIPTION } from "./shared/configs/globalErrors.config";

type FailedRequest = {
  resolve: (value: unknown) => void
  reject: (error: unknown) => void
  config: InternalAxiosRequestConfig
};

const axiosInstance = axios.create({
  baseURL: getApiUrl(""),
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

let onLogout: (() => void) | null = null;
let isRefreshing = false;
let failedRequestsQueue: FailedRequest[] = [];

export const setAxiosLogoutHandler = (handler: () => void): void => {
  onLogout = handler;
};

const resetFailedRequestsQueue = (): void => {
  failedRequestsQueue = [];
};

const addToFailedRequestsQueue = ({
  resolve,
  reject,
  config,
}: FailedRequest): void => {
  failedRequestsQueue.push({
    resolve,
    reject,
    config,
  });
};

const processQueue = (error: AxiosError | null, token: string | null = null): void => {
  failedRequestsQueue.forEach(({ resolve, reject, config }) => {
    if (error) {
      reject(error);
    }
    else {
      if (token && config.headers) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
      resolve(axiosInstance(config));
    }
  });

  resetFailedRequestsQueue();
};

const bearerToken = (authorization: unknown): string | undefined =>
  typeof authorization === "string" && authorization.startsWith("Bearer ")
    ? authorization.slice("Bearer ".length)
    : undefined;

const performTokenRefresh = async (rejectedToken?: string): Promise<string> => {
  return refreshToken(rejectedToken);
};

axiosInstance.interceptors.request.use(
  (config) => {
    const accessToken = getAccessToken();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Canceled requests (e.g. AbortController on unmount) are deliberate, not failures.
    if (axios.isCancel(error)) return Promise.reject(error);

    if (error.response?.status !== 401 && error.response?.status !== 403) {
      Sentry.captureException(error, {
        tags: {
          type: "api_error",
          status: String(error.response?.status),
          url: error.config?.url ?? "unknown",
        },
        extra: {
          requestUrl: error.config?.url,
          requestMethod: error.config?.method,
          responseStatus: error.response?.status,
          responseData: error.response?.data,
        },
      });
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      // If memory already holds a different token than the one the failed request
      // used (a refresh, here or published by another tab, landed while this request
      // was in flight), retry with that token directly instead of refreshing again.
      const storedToken = getAccessToken();
      const failedAuthHeader = originalRequest.headers?.Authorization;
      if (storedToken && failedAuthHeader && failedAuthHeader !== `Bearer ${storedToken}`) {
        originalRequest._retry = true;
        originalRequest.headers.Authorization = `Bearer ${storedToken}`;
        return axiosInstance(originalRequest);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => addToFailedRequestsQueue({
          resolve,
          reject,
          config: originalRequest,
        }));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await performTokenRefresh(bearerToken(originalRequest.headers?.Authorization));
        originalRequest.headers.Authorization = `Bearer ${newToken}`;

        const response = await axiosInstance(originalRequest);
        processQueue(null, newToken);
        return response;
      }
      catch (refreshError) {
        const authError = refreshError as AxiosError & { __isAuthFailure?: boolean };
        authError.__isAuthFailure = true;
        processQueue(authError, null);
        if (onLogout) onLogout();
        return Promise.reject(authError);
      }
      finally {
        isRefreshing = false;
      }
    }

    // 401 on a retried request = auth is truly dead
    if (error.response?.status === 401 && originalRequest._retry) {
      const authError = error as AxiosError & { __isAuthFailure?: boolean };
      authError.__isAuthFailure = true;
      return Promise.reject(authError);
    }

    if (error.response?.status === 403) {
      const isMutation = ["post", "put", "patch", "delete"].includes(
        error.config?.method?.toLowerCase() ?? "",
      );
      if (isMutation) {
        toast.error(GLOBAL_FORBIDDEN_ACTION_ERROR_MESSAGE, {
          id: "forbidden-error",
          description: GLOBAL_FORBIDDEN_ERROR_DESCRIPTION,
          duration: Infinity,
          closeButton: true,
        });
      }
      return Promise.reject(error);
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
